import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import DatePickerField from '../../components/common/DatePickerField';
import EvidenceUploader from '../../components/common/EvidenceUploader';
import incidentService from '../../services/incidentService';
import projectService from '../../services/projectService';
import './Incidents.css';

const CATEGORY_OPTIONS = [
  { id: 'osh', label: 'Occupational Safety & Health (OHS)', icon: 'fa-hard-hat', color: '#f59e0b' },
  { id: 'environmental', label: 'Environmental Incident', icon: 'fa-leaf', color: '#16a34a' },
  { id: 'community', label: 'Community/Stakeholder', icon: 'fa-users', color: '#3b82f6' },
  { id: 'labor', label: 'Labor Rights Violation', icon: 'fa-gavel', color: '#8b5cf6' },
  { id: 'security', label: 'Security Incident', icon: 'fa-shield-alt', color: '#ef4444' },
  { id: 'property', label: 'Property Damage', icon: 'fa-building', color: '#6b7280' },
];

// Descriptions are intentionally category-neutral (not OHS-specific wording
// like "death of worker") since severity applies across every incident
// category - OHS, Environmental, Community/Stakeholder, Labor, Security, and
// Property all share the same 5 levels but mean different things in context.
const SEVERITY_OPTIONS = [
  { value: 'fatal', label: 'Fatal', description: 'Loss of life, or catastrophic and irreversible harm', color: '#7f1d1d' },
  { value: 'major', label: 'Major/Serious', description: 'Severe, lasting harm or damage; significant violation', color: '#ef4444' },
  { value: 'moderate', label: 'Moderate', description: 'Notable harm or damage requiring corrective action', color: '#f59e0b' },
  { value: 'minor', label: 'Minor', description: 'Limited harm or damage, easily remedied', color: '#3b82f6' },
  { value: 'near_miss', label: 'Near Miss', description: 'No harm or damage occurred, but could have', color: '#6b7280' },
];

const STATUS_OPTIONS = ['reported', 'investigating', 'under_review', 'action_required', 'closed'];

const formatStatus = (status) =>
  String(status || '').split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const mapIncident = (incident) => ({
  ...incident,
  date: incident.occurredAt ? incident.occurredAt.slice(0, 10) : '',
  project: incident.project || { id: incident.projectId, name: 'Project' },
  reportedBy: {
    name: incident.reporter
      ? `${incident.reporter.firstName} ${incident.reporter.lastName}`.trim()
      : 'Reporter',
    role: incident.reporter?.role || '',
  },
});

const Incidents = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [showReportModal, setShowReportModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [evidenceIncident, setEvidenceIncident] = useState(null);
  const [editingIncident, setEditingIncident] = useState(null);

  const fetchIncidents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSeverity !== 'all') params.severity = filterSeverity;
      const data = await incidentService.getAll(params);
      setIncidents((data.incidents || []).map(mapIncident));
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      toast.error(error.response?.data?.error || 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const stats = {
    total: incidents.length,
    open: incidents.filter((i) => i.status !== 'closed').length,
    major: incidents.filter((i) => i.severity === 'major' || i.severity === 'fatal').length,
    investigating: incidents.filter((i) => i.status === 'investigating').length,
    daysSinceLastIncident: incidents.length
      ? Math.max(0, Math.floor((Date.now() - new Date(incidents[0].occurredAt).getTime()) / (1000 * 60 * 60 * 24)))
      : 0,
  };

  const filteredIncidents = incidents.filter((incident) => {
    const q = searchQuery.toLowerCase();
    return !q
      || incident.title.toLowerCase().includes(q)
      || incident.incidentNumber.toLowerCase().includes(q)
      || (incident.project?.name || '').toLowerCase().includes(q);
  });

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'fatal': return 'severity-fatal';
      case 'major': return 'severity-major';
      case 'moderate': return 'severity-moderate';
      case 'minor': return 'severity-minor';
      case 'near_miss': return 'severity-near-miss';
      default: return '';
    }
  };

  const getSeverityLabel = (severity) => (
    SEVERITY_OPTIONS.find((s) => s.value === severity)?.label || severity
  );

  const getStatusClass = (status) => {
    switch (status) {
      case 'reported': return 'status-reported';
      case 'investigating': return 'status-investigating';
      case 'under_review': return 'status-under-review';
      case 'action_required': return 'status-action-required';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  const handleCreateIncident = async (incidentData) => {
    try {
      setSaving(true);
      await incidentService.create(incidentData);
      toast.success('Incident reported successfully');
      setShowReportModal(false);
      fetchIncidents();
    } catch (error) {
      console.error('Failed to report incident:', error);
      toast.error(error.response?.data?.error || 'Failed to report incident');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateIncident = async (incidentData) => {
    if (!editingIncident) return;
    try {
      setSaving(true);
      await incidentService.update(editingIncident.id, incidentData);
      toast.success('Incident updated successfully');
      setEditingIncident(null);
      fetchIncidents();
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast.error(error.response?.data?.error || 'Failed to update incident');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (incident, nextStatus) => {
    try {
      await incidentService.update(incident.id, { status: nextStatus });
      toast.success('Incident status updated');
      fetchIncidents();
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast.error(error.response?.data?.error || 'Failed to update incident');
    }
  };

  const handleDeleteIncident = async (incident) => {
    if (!window.confirm(`Delete incident ${incident.incidentNumber}? This cannot be undone.`)) return;
    try {
      await incidentService.delete(incident.id);
      toast.success('Incident deleted');
      fetchIncidents();
    } catch (error) {
      console.error('Failed to delete incident:', error);
      toast.error(error.response?.data?.error || 'Failed to delete incident');
    }
  };

  const renderActions = (incident) => (
    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
      <button className="btn-icon" title="Edit" onClick={() => setEditingIncident(incident)}>
        <i className="fas fa-edit"></i>
      </button>
      <button className="btn-icon" title="Evidence" onClick={() => setEvidenceIncident(incident)}>
        <i className="fas fa-camera"></i>
      </button>
      <select
        className="filter-select"
        value={incident.status}
        onChange={(e) => handleStatusChange(incident, e.target.value)}
        style={{ minWidth: '145px' }}
      >
        {STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>{formatStatus(status)}</option>
        ))}
      </select>
      <button className="btn-icon danger" title="Delete" onClick={() => handleDeleteIncident(incident)}>
        <i className="fas fa-trash"></i>
      </button>
    </div>
  );

  return (
    <div className="incidents-page">
      <div className="incidents-header">
        <div className="header-content">
          <div className="header-title">
            <h1><i className="fas fa-exclamation-triangle"></i> Incidents</h1>
            <p className="header-subtitle">Report and track occupational, environmental, and community incidents</p>
          </div>
          <button className="btn-primary-green" onClick={() => setShowReportModal(true)}>
            <i className="fas fa-plus"></i> Report Incident
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-clipboard-list"></i></div>
          <div className="stat-details">
            <div className="stat-label">Total Incidents</div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-trend">{stats.open} open</div>
          </div>
        </div>
        <div className="stat-card danger">
          <div className="stat-icon"><i className="fas fa-exclamation-circle"></i></div>
          <div className="stat-details">
            <div className="stat-label">Major/Fatal</div>
            <div className="stat-value">{stats.major}</div>
            <div className="stat-trend">Requires immediate attention</div>
          </div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><i className="fas fa-search"></i></div>
          <div className="stat-details">
            <div className="stat-label">Investigating</div>
            <div className="stat-value">{stats.investigating}</div>
            <div className="stat-trend">Active investigations</div>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><i className="fas fa-calendar-check"></i></div>
          <div className="stat-details">
            <div className="stat-label">Days Since Last</div>
            <div className="stat-value">{stats.daysSinceLastIncident}</div>
            <div className="stat-trend">Last incident</div>
          </div>
        </div>
      </div>

      <div className="incidents-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search incidents by title, number or project..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="controls-row">
          <div className="controls-left">
            <div className="filter-group">
              {['all', 'investigating', 'action_required', 'closed'].map((status) => (
                <button
                  key={status}
                  className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status === 'all' ? 'All' : formatStatus(status)}
                </button>
              ))}
            </div>
          </div>

          <div className="controls-right">
            <select className="filter-select" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
              <option value="all">All Severity</option>
              {SEVERITY_OPTIONS.map((level) => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>

            <div className="view-toggle">
              <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><i className="fas fa-list"></i></button>
              <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><i className="fas fa-th"></i></button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading incidents...</h3>
        </div>
      ) : filteredIncidents.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-check-circle"></i>
          <h3>No incidents found</h3>
          <p>No incidents match your current filters</p>
        </div>
      ) : (
        <div className={`incidents-container ${viewMode}`}>
          {filteredIncidents.map((incident) => (
            viewMode === 'list' ? (
              <div key={incident.id} className="incident-item">
                <div className="incident-severity-indicator">
                  <div className={`severity-bar ${getSeverityClass(incident.severity)}`}></div>
                </div>
                <div className="incident-content">
                  <div className="incident-header">
                    <div className="incident-title-section">
                      <h3 className="incident-title">{incident.title}</h3>
                      <span className="incident-number">{incident.incidentNumber}</span>
                    </div>
                    <div className="incident-badges">
                      <span className={`severity-badge ${getSeverityClass(incident.severity)}`}>{getSeverityLabel(incident.severity)}</span>
                      <span className={`status-badge ${getStatusClass(incident.status)}`}>{formatStatus(incident.status)}</span>
                      <span className="category-badge">{incident.category}</span>
                    </div>
                  </div>
                  <div className="incident-meta">
                    <div className="meta-item"><i className="fas fa-project-diagram"></i><span>{incident.project?.name}</span></div>
                    <div className="meta-item"><i className="fas fa-calendar"></i><span>{new Date(incident.occurredAt).toLocaleDateString()}</span></div>
                    <div className="meta-item"><i className="fas fa-map-marker-alt"></i><span>{incident.location || '—'}</span></div>
                    <div className="meta-item"><i className="fas fa-user"></i><span>Reported by: {incident.reportedBy.name}</span></div>
                    {incident.peopleInjured > 0 && (
                      <div className="meta-item injury"><i className="fas fa-user-injured"></i><span>{incident.peopleInjured} injured</span></div>
                    )}
                  </div>
                  <p className="incident-description">{incident.description}</p>
                  {renderActions(incident)}
                </div>
              </div>
            ) : (
              <div key={incident.id} className="incident-card">
                <div className={`incident-card-severity ${getSeverityClass(incident.severity)}`}></div>
                <div className="incident-card-content">
                  <h3 className="incident-card-title">{incident.title}</h3>
                  <span className="incident-card-number">{incident.incidentNumber}</span>
                  <div className="incident-card-badges">
                    <span className={`severity-badge ${getSeverityClass(incident.severity)}`}>{getSeverityLabel(incident.severity)}</span>
                    <span className={`status-badge ${getStatusClass(incident.status)}`}>{formatStatus(incident.status)}</span>
                  </div>
                  <div className="incident-card-meta">
                    <div className="meta-item"><i className="fas fa-project-diagram"></i><span>{incident.project?.name}</span></div>
                    <div className="meta-item"><i className="fas fa-calendar"></i><span>{new Date(incident.occurredAt).toLocaleDateString()}</span></div>
                    <div className="meta-item"><i className="fas fa-map-marker-alt"></i><span>{incident.location || '—'}</span></div>
                  </div>
                  <p className="incident-card-description">{incident.description}</p>
                  {renderActions(incident)}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {showReportModal && (
        <ReportIncidentModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onSave={handleCreateIncident}
          projects={projects}
          saving={saving}
        />
      )}

      {editingIncident && (
        <ReportIncidentModal
          isOpen={!!editingIncident}
          onClose={() => setEditingIncident(null)}
          onSave={handleUpdateIncident}
          projects={projects}
          saving={saving}
          incident={editingIncident}
        />
      )}

      <EvidenceUploader
        isOpen={!!evidenceIncident}
        onClose={() => setEvidenceIncident(null)}
        title="Incident Evidence"
        subtitle={evidenceIncident ? `${evidenceIncident.incidentNumber} — ${evidenceIncident.title}` : ''}
        reloadKey={evidenceIncident?.id}
        fetchEvidence={async () => {
          const data = await incidentService.getEvidence(evidenceIncident.id);
          return data.evidence;
        }}
        uploadEvidence={async (file, fileType) => {
          const data = await incidentService.uploadEvidence(evidenceIncident.id, file, fileType);
          return data.evidence;
        }}
        deleteEvidence={(evidenceId) => incidentService.deleteEvidence(evidenceId)}
      />
    </div>
  );
};

const ReportIncidentModal = ({ isOpen, onClose, onSave, projects, saving, incident }) => {
  const now = new Date();
  const currentDate = now.toISOString().split('T')[0];
  const currentTime = now.toTimeString().slice(0, 5);
  const isEditing = !!incident;

  const [formData, setFormData] = useState(() => {
    if (!incident) {
      return {
        title: '',
        category: '',
        severity: '',
        projectId: '',
        date: currentDate,
        time: currentTime,
        location: '',
        description: '',
        peopleInjured: '0',
        peopleAffected: '',
        witnessName: '',
        witnessContact: '',
        immediateActions: '',
      };
    }
    const occurred = incident.occurredAt ? new Date(incident.occurredAt) : now;
    return {
      title: incident.title || '',
      category: incident.category || '',
      severity: incident.severity || '',
      projectId: incident.projectId || '',
      date: occurred.toISOString().slice(0, 10),
      time: occurred.toTimeString().slice(0, 5),
      location: incident.location || '',
      description: incident.description || '',
      peopleInjured: String(incident.peopleInjured ?? 0),
      peopleAffected: incident.peopleAffected || '',
      witnessName: incident.witnessName || '',
      witnessContact: incident.witnessContact || '',
      immediateActions: incident.immediateActions || '',
    };
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="incident-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-exclamation-triangle"></i> {isEditing ? `Edit Incident ${incident.incidentNumber}` : 'Report Incident'}</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-section">
              <h3 className="section-title">Incident Details</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Incident Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., Worker Fall from Scaffolding" required />
                </div>

                <div className="form-group">
                  <label>Incident Category *</label>
                  <Select
                    name="category"
                    value={CATEGORY_OPTIONS.find((c) => c.id === formData.category) ? {
                      label: CATEGORY_OPTIONS.find((c) => c.id === formData.category).label,
                      value: formData.category,
                    } : null}
                    onChange={(selected) => setFormData((prev) => ({ ...prev, category: selected?.value || '' }))}
                    options={CATEGORY_OPTIONS.map((c) => ({
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className={`fas ${c.icon}`} style={{ color: c.color }}></i>{c.label}
                        </div>
                      ),
                      value: c.id,
                    }))}
                    placeholder="Select category..."
                    className="category-select"
                    classNamePrefix="select"
                  />
                </div>

                <div className="form-group">
                  <label>Severity Level *</label>
                  <select name="severity" value={formData.severity} onChange={handleInputChange} required>
                    <option value="">Select severity...</option>
                    {SEVERITY_OPTIONS.map((level) => (
                      <option key={level.value} value={level.value}>{level.label} - {level.description}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Project *</label>
                  <Select
                    name="projectId"
                    value={projects.find((p) => p.id === formData.projectId)
                      ? { label: `${projects.find((p) => p.id === formData.projectId).name} (${projects.find((p) => p.id === formData.projectId).code || 'No code'})`, value: formData.projectId }
                      : null}
                    onChange={(selected) => setFormData((prev) => ({ ...prev, projectId: selected?.value || '' }))}
                    options={projects.map((p) => ({ label: `${p.name} (${p.code || 'No code'})`, value: p.id }))}
                    placeholder="Select project..."
                    isSearchable
                    className="project-select"
                    classNamePrefix="select"
                  />
                </div>

                <div className="form-group">
                  <label>Date of Incident *</label>
                  <DatePickerField name="date" value={formData.date} onChange={handleInputChange} maxDate={currentDate} placeholder="Select date" required />
                </div>

                <div className="form-group">
                  <label>Time of Incident *</label>
                  <input type="time" name="time" value={formData.time} onChange={handleInputChange} required />
                </div>

                <div className="form-group full-width">
                  <label>Location/Area *</label>
                  <input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="e.g., Site A - Section 3, Equipment Yard" required />
                  <small className="form-hint">Specific location where incident occurred</small>
                </div>

                <div className="form-group full-width">
                  <label>Incident Description *</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe what happened, how it happened, and the sequence of events..." rows="4" required />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title"><i className="fas fa-users"></i> People Affected</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Number of People Injured</label>
                  <input type="number" name="peopleInjured" value={formData.peopleInjured} onChange={handleInputChange} min="0" placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Names of Injured/Affected</label>
                  <input type="text" name="peopleAffected" value={formData.peopleAffected} onChange={handleInputChange} placeholder="Names of people involved" />
                </div>
                <div className="form-group">
                  <label>Witness Name</label>
                  <input type="text" name="witnessName" value={formData.witnessName} onChange={handleInputChange} placeholder="Name of witness" />
                </div>
                <div className="form-group">
                  <label>Witness Contact</label>
                  <input type="text" name="witnessContact" value={formData.witnessContact} onChange={handleInputChange} placeholder="Phone or email" />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title"><i className="fas fa-first-aid"></i> Immediate Actions Taken</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Actions Taken Immediately After Incident *</label>
                  <textarea name="immediateActions" value={formData.immediateActions} onChange={handleInputChange} placeholder="Describe immediate response: first aid provided, area secured, emergency services called, etc." rows="3" required />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title"><i className="fas fa-camera"></i> Evidence & Photos</h3>
              <p className="form-hint">After saving the incident, use the camera icon on the incident row to attach photos or documents.</p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              <i className="fas fa-check"></i> {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Report Incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Incidents;
